import { MachineConfig } from '@eneco/flows/types';
import { assign, setup } from 'xstate';

import { actors } from './getOffer.actor';
import { OfferErrorMessage } from './getOffer.errors';
import { GetOfferEvent } from './getOffer.helpers';
import { SmeOffer } from './getOffer.types';
import { FlowContextSme } from '../../context';

// Shared by every invoke's `onError` below — xstate surfaces a rejected actor's error as `event.error`,
// with the message set by the actor (see getOffer.errors.ts) identifying which failure occurred.
const matchesErrorMessage =
  (message: string) =>
  ({ event }: { event: unknown }) =>
    (event as { error?: { message?: string } })?.error?.message === message;

const getOfferErrorTransitions = [
  { target: 'ERROR_NOT_FOUND', guard: matchesErrorMessage(OfferErrorMessage.NotFound) },
  { target: 'ERROR_NOT_AVAILABLE', guard: matchesErrorMessage(OfferErrorMessage.SalesCapReached) },
  { target: 'ERROR_NOT_AUTHENTICATED', guard: matchesErrorMessage(OfferErrorMessage.Unauthorized) },
  { target: 'ERROR' },
];

export const getOfferMachine = (fetchedOffers?: SmeOffer) =>
  setup({
    types: {} as {
      input: MachineConfig;
      context: MachineConfig & {
        errorMessage?: string;
        flowContext: FlowContextSme;
        offer?: SmeOffer;
      };
      events: GetOfferEvent | { type: 'CACHE_HIT' };
    },
    actors,
  }).createMachine({
    id: 'getOffer',
    initial: 'IDLE',
    context: ({ input }) => ({
      businessUnit: input.businessUnit,
      label: input.label,
      flowContext: {} as FlowContextSme,
      offer: fetchedOffers,
    }),
    states: {
      IDLE: {
        on: {
          GET_OFFER: {
            target: 'FETCHING',
            actions: assign(({ context, event }) => ({
              ...context,
              flowContext: event.flowContext,
            })),
          },
          // Reuse the response seeded from the flow context (via fetchedOffers) without a network call.
          CACHE_HIT: {
            target: 'SUCCESS',
          },
        },
      },
      FETCHING: {
        invoke: {
          src: 'getOffer',
          input: ({ context }) => ({
            businessUnit: context.businessUnit,
            label: context.label,
            values: context.flowContext,
          }),
          onDone: {
            target: 'SUCCESS',
            actions: assign(({ context, event }) => ({
              ...context,
              offer: event.output,
            })),
          },
          onError: getOfferErrorTransitions,
        },
      },
      SUCCESS: {
        entry: [
          assign(({ context }) => ({
            ...context,
            errorMessage: undefined,
          })),
        ],
      },
      ERROR: {},
      ERROR_NOT_FOUND: {},
      ERROR_NOT_AVAILABLE: {},
      ERROR_NOT_AUTHENTICATED: {},
    },
  });
